import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { RefreshCwIcon } from 'lucide-react'
import { useState } from 'react'
import { ScreenHeader } from '@app/components/screen-header'
import { Button } from '@app/components/ui/button'
import { useCanViewCari, useCanViewPurchasing } from '@app/features/auth/api'
import {
  DASHBOARD_DEFAULTS,
  dashboardQueryKey,
  fetchDashboardSummary,
  formatClock,
  formatDate,
} from '@app/features/dashboard/api'
import { OutputBars } from '@app/features/dashboard/output-bars'
import { RecentTables } from '@app/features/dashboard/recent-tables'
import { DashboardError, DashboardSkeleton } from '@app/features/dashboard/states'
import { StatTiles } from '@app/features/dashboard/stat-tiles'
import { WipRoute } from '@app/features/dashboard/wip-route'
import { cn } from '@app/lib/cn'

/**
 * Panel — the factory's morning screen.
 *
 * The inverse of Korgun's "fill the form first, then look" pattern: today's
 * numbers appear the moment the screen opens, no configuration needed. One
 * request (`/api/dashboard/summary`), refreshed every 60 seconds; stale data
 * stays in place while a refetch is in flight.
 */

const REFRESH_INTERVAL_MS = 60_000

export function DashboardScreen() {
  const [dayCount, setDayCount] = useState(DASHBOARD_DEFAULTS.days)
  const params = { ...DASHBOARD_DEFAULTS, days: dayCount }
  /*
   * The tiles themselves are drawn from what the payload CONTAINS (a response
   * carries a finance block only for a role that may have it). The role is
   * consulted for the two things that cannot wait for the payload: the
   * skeleton, which has to guess the tile count before the answer arrives, and
   * the subtitle, which promises what the screen will hold. Both read the
   * session already in the cache from the route guard, so neither costs a
   * request.
   */
  const canViewCari = useCanViewCari()
  const canViewPurchasing = useCanViewPurchasing()
  // Four operational tiles, plus one per finance block this role gets.
  const tileCount = 4 + (canViewCari ? 1 : 0) + (canViewPurchasing ? 1 : 0)

  const { data, error, isPending, isFetching, dataUpdatedAt, refetch } = useQuery({
    queryKey: dashboardQueryKey(params),
    queryFn: () => fetchDashboardSummary(params),
    refetchInterval: REFRESH_INTERVAL_MS,
    placeholderData: keepPreviousData,
  })

  /*
   * The subtitle names what is actually on the screen, so it ends at the last
   * thing this role will find — promising a figure they cannot see is worse
   * than a shorter sentence. Written once, in lower case, because it appears
   * both after the date ("04.08.2026 · açık siparişler…") and on its own, and
   * the two must not drift apart.
   */
  const covers = canViewCari
    ? 'açık siparişler, üretim hattı, stok ve cari yük tek ekranda'
    : canViewPurchasing
      ? 'açık siparişler, üretim hattı, stok ve satınalma yükü tek ekranda'
      : 'açık siparişler, üretim hattı ve stok tek ekranda'
  const coversSentence = `${covers.charAt(0).toLocaleUpperCase('tr-TR')}${covers.slice(1)}.`

  const header = (
    <ScreenHeader
      title="Bugün atölyede"
      description={data ? `${formatDate(data.today)} · ${covers}.` : coversSentence}
      actions={
        <>
          {/*
            The screen reloads itself every 60 seconds, so a tab left open on a
            wall display looks identical whether it is live or died an hour ago.
            The stamp is the only thing that tells them apart. `dataUpdatedAt`
            is 0 until the first successful answer.
          */}
          {dataUpdatedAt > 0 ? (
            <span className="font-mono text-micro tnum text-ink-muted">
              Son güncelleme {formatClock(dataUpdatedAt)}
            </span>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
            aria-busy={isFetching}
            title="Verileri yeniden yükle"
          >
            <RefreshCwIcon className={cn(isFetching && 'animate-spin')} aria-hidden />
            {isFetching ? 'Yenileniyor' : 'Yenile'}
          </Button>
        </>
      }
    />
  )

  if (isPending) {
    return (
      <>
        {header}
        <DashboardSkeleton tileCount={tileCount} />
      </>
    )
  }

  if (error || !data) {
    return (
      <>
        {header}
        <DashboardError
          message={
            error instanceof Error
              ? error.message
              : 'Sunucuya ulaşılamadı. Bağlantıyı denetleyip yeniden deneyin.'
          }
          onRetry={() => void refetch()}
          refreshing={isFetching}
        />
      </>
    )
  }

  return (
    <>
      {header}

      <div className="flex flex-col gap-3">
        <StatTiles summary={data} />

        <div className="grid min-w-0 gap-3 xl:grid-cols-3">
          <div className="min-w-0 xl:col-span-2">
            <WipRoute production={data.production} />
          </div>
          <OutputBars
            output={data.output}
            dayCount={data.dayCount}
            onDayCountChange={setDayCount}
            refreshing={isFetching}
          />
        </div>

        <RecentTables orders={data.recentOrders} emirs={data.recentEmirs} />
      </div>
    </>
  )
}
